import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReactApexChart from 'react-apexcharts';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Typography,
    Chip,
    IconButton,
    Alert,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    ButtonGroup,
    Tooltip,
    LinearProgress
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';

// icons
import {
    IconArrowLeft,
    IconChartLine,
    IconChartBar,
    IconChartArea,
    IconClock,
    IconAlertCircle,
    IconCircleCheck,
    IconTrendingUp,
    IconTrendingDown,
    IconRefresh,
    IconDownload,
    IconInfoCircle,
    IconBolt,
    IconFlame,
    IconDroplet,
    IconServer
} from '@tabler/icons-react';

// Generate mock time series data
const generateTimeSeriesData = (hours = 24, metric = 'executions') => {
    const now = new Date();
    const data = [];

    for (let i = hours; i >= 0; i--) {
        const timestamp = new Date(now - i * 60 * 60 * 1000);
        let value = 0;

        switch (metric) {
            case 'executions':
                value = Math.floor(Math.random() * 100) + 20;
                break;
            case 'errors':
                value = Math.floor(Math.random() * 5);
                break;
            case 'duration':
                value = Math.floor(Math.random() * 50) + 10;
                break;
            case 'memory':
                value = Math.floor(Math.random() * 128) + 64;
                break;
            case 'concurrency':
                value = Math.floor(Math.random() * 10) + 1;
                break;
            case 'throttles':
                value = Math.floor(Math.random() * 3);
                break;
            default:
                value = Math.floor(Math.random() * 100);
        }

        data.push({
            x: timestamp.getTime(),
            y: value
        });
    }

    return data;
};

// ==============================|| FUNCTION METRICS ||============================== //

const FunctionMetrics = () => {
    const theme = useTheme();
    const { functionId } = useParams();
    const navigate = useNavigate();
    const { functions } = useSelector((state) => state.lambdaFunctions);

    const [func, setFunc] = useState(null);
    const [timeRange, setTimeRange] = useState('24h');
    const [chartType, setChartType] = useState('line');
    const [loading, setLoading] = useState(false);
    const [selectedMetrics, setSelectedMetrics] = useState(['executions', 'errors']);

    useEffect(() => {
        const foundFunc = functions.find((f) => f.id === functionId);
        if (foundFunc) {
            setFunc(foundFunc);
        }
    }, [functionId, functions]);

    const handleRefresh = () => {
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
        }, 1000);
    };

    const handleExport = () => {
        // Export metrics data
        const data = {
            function: func.name,
            timeRange,
            metrics: selectedMetrics,
            timestamp: new Date().toISOString()
        };

        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${func.name}_metrics_${new Date().toISOString()}.json`;
        a.click();
    };

    const getTimeRangeHours = () => {
        switch (timeRange) {
            case '1h':
                return 1;
            case '24h':
                return 24;
            case '7d':
                return 168;
            case '30d':
                return 720;
            default:
                return 24;
        }
    };

    // Chart options
    const getChartOptions = (title, color, yAxisFormatter) => {
        const isDark = theme.palette.mode === 'dark';

        return {
            chart: {
                type: chartType,
                height: 350,
                toolbar: {
                    show: true
                },
                background: 'transparent',
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 800
                }
            },
            colors: [color],
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'smooth',
                width: 2
            },
            fill: {
                type: chartType === 'area' ? 'gradient' : 'solid',
                gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.7,
                    opacityTo: 0.3,
                    stops: [0, 90, 100]
                }
            },
            grid: {
                borderColor: isDark ? '#333' : '#e0e0e0',
                strokeDashArray: 4
            },
            xaxis: {
                type: 'datetime',
                labels: {
                    style: {
                        colors: theme.palette.text.secondary
                    }
                }
            },
            yaxis: {
                title: {
                    text: title,
                    style: {
                        color: theme.palette.text.secondary
                    }
                },
                labels: {
                    style: {
                        colors: theme.palette.text.secondary
                    },
                    formatter: yAxisFormatter
                }
            },
            tooltip: {
                theme: isDark ? 'dark' : 'light',
                x: {
                    format: 'dd MMM HH:mm'
                }
            },
            title: {
                text: title,
                align: 'left',
                style: {
                    fontSize: '16px',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                }
            }
        };
    };

    if (!func) {
        return (
            <MainCard>
                <Alert severity="error">Function not found</Alert>
            </MainCard>
        );
    }

    // Calculate summary statistics
    const stats = {
        totalExecutions: func.metrics.total_executions_24h,
        successRate: func.metrics.success_rate,
        avgDuration: func.metrics.avg_latency_ms,
        p95Duration: func.metrics.p95_latency_ms,
        p99Duration: func.metrics.p99_latency_ms,
        errors: func.metrics.errors_24h,
        errorRate: ((func.metrics.errors_24h / func.metrics.total_executions_24h) * 100).toFixed(2),
        lastExecution: func.metrics.last_execution
    };

    // Metric cards data
    const metricCards = [
        {
            title: 'Total Executions',
            value: stats.totalExecutions.toLocaleString(),
            icon: <IconBolt />,
            color: theme.palette.primary.main,
            trend: '+12.5%',
            trendUp: true
        },
        {
            title: 'Success Rate',
            value: `${stats.successRate}%`,
            icon: <IconCircleCheck />,
            color: theme.palette.success.main,
            trend: '+0.8%',
            trendUp: true
        },
        {
            title: 'Avg Duration',
            value: `${stats.avgDuration}ms`,
            icon: <IconClock />,
            color: theme.palette.info.main,
            trend: '-5.2%',
            trendUp: false
        },
        {
            title: 'Error Rate',
            value: `${stats.errorRate}%`,
            icon: <IconAlertCircle />,
            color: theme.palette.error.main,
            trend: '-2.1%',
            trendUp: false
        }
    ];

    // Performance insights
    const insights = [
        {
            type: stats.successRate > 99 ? 'success' : 'warning',
            title: 'Reliability',
            message:
                stats.successRate > 99
                    ? 'Function is performing excellently with 99%+ success rate'
                    : `Success rate is ${stats.successRate}%. Consider investigating error logs.`
        },
        {
            type: stats.avgDuration < 100 ? 'success' : 'info',
            title: 'Performance',
            message:
                stats.avgDuration < 100
                    ? 'Function executes quickly with low latency'
                    : 'Consider optimizing function code to reduce execution time'
        },
        {
            type: stats.errors < 50 ? 'success' : 'error',
            title: 'Errors',
            message:
                stats.errors < 50 ? 'Error rate is within acceptable limits' : 'High error count detected. Review error logs for patterns.'
        }
    ];

    return (
        <MainCard
            title={
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconButton onClick={() => navigate('/lambda/dashboard')}>
                            <IconArrowLeft />
                        </IconButton>
                        <Typography variant="h3">Metrics: {func.name}</Typography>
                        <Chip label={`v${func.version}`} size="small" />
                    </Box>
                    <Box display="flex" gap={1}>
                        <ButtonGroup size="small">
                            <Button variant={timeRange === '1h' ? 'contained' : 'outlined'} onClick={() => setTimeRange('1h')}>
                                1H
                            </Button>
                            <Button variant={timeRange === '24h' ? 'contained' : 'outlined'} onClick={() => setTimeRange('24h')}>
                                24H
                            </Button>
                            <Button variant={timeRange === '7d' ? 'contained' : 'outlined'} onClick={() => setTimeRange('7d')}>
                                7D
                            </Button>
                            <Button variant={timeRange === '30d' ? 'contained' : 'outlined'} onClick={() => setTimeRange('30d')}>
                                30D
                            </Button>
                        </ButtonGroup>

                        <Tooltip title="Export Metrics">
                            <IconButton onClick={handleExport}>
                                <IconDownload />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Refresh">
                            <IconButton onClick={handleRefresh} disabled={loading}>
                                <IconRefresh />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            }
        >
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Summary Cards */}
            <Grid container spacing={gridSpacing} sx={{ mb: 3 }}>
                {metricCards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                            {card.title}
                                        </Typography>
                                        <Typography variant="h3">{card.value}</Typography>
                                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                            {card.trendUp ? (
                                                <IconTrendingUp size={16} color={theme.palette.success.main} />
                                            ) : (
                                                <IconTrendingDown size={16} color={theme.palette.error.main} />
                                            )}
                                            <Typography variant="caption" color={card.trendUp ? 'success.main' : 'error.main'}>
                                                {card.trend}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            backgroundColor: `${card.color}20`,
                                            borderRadius: '12px',
                                            p: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {React.cloneElement(card.icon, {
                                            size: 24,
                                            style: { color: card.color }
                                        })}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Performance Insights */}
            <Grid container spacing={gridSpacing} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Performance Insights
                            </Typography>
                            <List>
                                {insights.map((insight, index) => (
                                    <React.Fragment key={index}>
                                        <ListItem>
                                            <ListItemIcon>
                                                {insight.type === 'success' && <IconCircleCheck color={theme.palette.success.main} />}
                                                {insight.type === 'warning' && <IconAlertCircle color={theme.palette.warning.main} />}
                                                {insight.type === 'error' && <IconAlertCircle color={theme.palette.error.main} />}
                                                {insight.type === 'info' && <IconInfoCircle color={theme.palette.info.main} />}
                                            </ListItemIcon>
                                            <ListItemText primary={insight.title} secondary={insight.message} />
                                        </ListItem>
                                        {index < insights.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={gridSpacing}>
                <Grid item xs={12} lg={6}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                <Typography variant="h5">Invocations</Typography>
                                <ButtonGroup size="small">
                                    <Tooltip title="Line Chart">
                                        <Button
                                            variant={chartType === 'line' ? 'contained' : 'outlined'}
                                            onClick={() => setChartType('line')}
                                        >
                                            <IconChartLine size={18} />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Area Chart">
                                        <Button
                                            variant={chartType === 'area' ? 'contained' : 'outlined'}
                                            onClick={() => setChartType('area')}
                                        >
                                            <IconChartArea size={18} />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Bar Chart">
                                        <Button
                                            variant={chartType === 'bar' ? 'contained' : 'outlined'}
                                            onClick={() => setChartType('bar')}
                                        >
                                            <IconChartBar size={18} />
                                        </Button>
                                    </Tooltip>
                                </ButtonGroup>
                            </Box>
                            <ReactApexChart
                                options={getChartOptions('Invocations', theme.palette.primary.main, (value) =>
                                    Math.round(value).toLocaleString()
                                )}
                                series={[
                                    {
                                        name: 'Invocations',
                                        data: generateTimeSeriesData(getTimeRangeHours(), 'executions')
                                    }
                                ]}
                                type={chartType}
                                height={350}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Errors
                            </Typography>
                            <ReactApexChart
                                options={getChartOptions('Errors', theme.palette.error.main, (value) => Math.round(value).toLocaleString())}
                                series={[
                                    {
                                        name: 'Errors',
                                        data: generateTimeSeriesData(getTimeRangeHours(), 'errors')
                                    }
                                ]}
                                type={chartType}
                                height={350}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Duration
                            </Typography>
                            <ReactApexChart
                                options={getChartOptions('Duration (ms)', theme.palette.info.main, (value) => `${value}ms`)}
                                series={[
                                    {
                                        name: 'Avg Duration',
                                        data: generateTimeSeriesData(getTimeRangeHours(), 'duration')
                                    }
                                ]}
                                type={chartType}
                                height={350}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Memory Usage
                            </Typography>
                            <ReactApexChart
                                options={getChartOptions('Memory (MB)', theme.palette.warning.main, (value) => `${value}MB`)}
                                series={[
                                    {
                                        name: 'Memory Usage',
                                        data: generateTimeSeriesData(getTimeRangeHours(), 'memory')
                                    }
                                ]}
                                type={chartType}
                                height={350}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* Additional Metrics */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Advanced Metrics
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={4}>
                                    <Paper sx={{ p: 2 }}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <IconFlame size={20} color={theme.palette.warning.main} />
                                            <Typography variant="subtitle2">Cold Starts</Typography>
                                        </Box>
                                        <Typography variant="h4" sx={{ mt: 1 }}>
                                            12.3%
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            of total invocations
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Paper sx={{ p: 2 }}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <IconServer size={20} color={theme.palette.info.main} />
                                            <Typography variant="subtitle2">Concurrent Executions</Typography>
                                        </Box>
                                        <Typography variant="h4" sx={{ mt: 1 }}>
                                            8.5
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            average concurrent
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Paper sx={{ p: 2 }}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <IconDroplet size={20} color={theme.palette.error.main} />
                                            <Typography variant="subtitle2">Throttles</Typography>
                                        </Box>
                                        <Typography variant="h4" sx={{ mt: 1 }}>
                                            0.2%
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            of requests throttled
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default FunctionMetrics;
